import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { CheckCircle2, ArrowRight, Clock, Users, Shield, FileCheck } from 'lucide-react';
import { motion } from 'framer-motion';

const HERO_IMAGE = 'https://images.unsplash.com/photo-1758448511322-8bfc73daf606?w=1200&q=85';

const BATHROOM_IMAGE = 'https://images.unsplash.com/photo-1758548157466-7c454382035a?w=800&q=85';

export default function HomePage() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen" style={{ backgroundColor: 'var(--hr-bg)' }}>
      
      {/* HERO - ANGLE TRANCHANT */}
      <section className="relative overflow-hidden hero-gradient noise-overlay">
        <div className="max-w-[1160px] mx-auto px-4 sm:px-6 lg:px-8 pt-16 sm:pt-20 lg:pt-24 pb-16 sm:pb-20">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-center">
            
            {/* Left: Message FORT */}
            <div className="lg:col-span-6 space-y-6 z-10">
              <Badge 
                className="rounded-full px-4 py-1.5 font-['Montserrat'] text-xs font-medium uppercase tracking-wide"
                style={{ backgroundColor: 'var(--hr-accent)', color: 'var(--hr-accent-fg)' }}
                data-testid="hero-badge"
              >
                Rénovation pilotée en Suisse romande
              </Badge>
              
              <h1 
                className="font-['Playfair_Display'] text-4xl sm:text-5xl lg:text-6xl font-bold leading-[1.05] tracking-[-0.02em]"
                style={{ color: 'var(--hr-text)' }}
                data-testid="hero-title"
              >
                Vos travaux ne seront plus jamais un cauchemar
              </h1>
              
              <h2 
                className="font-['Figtree'] text-lg sm:text-xl leading-relaxed"
                style={{ color: 'var(--hr-text-muted)' }}
                data-testid="hero-subtitle"
              >
                Nous coordonnons tout, vous suivez sereinement. Un interlocuteur unique, un planning tenu, un résultat garanti.
              </h2>

              {/* CTA */}
              <div className="flex flex-col sm:flex-row gap-4 pt-4">
                <Button
                  size="lg"
                  className="rounded-xl px-8 py-6 text-base font-medium"
                  style={{ backgroundColor: 'var(--hr-primary)', color: 'var(--hr-primary-fg)' }}
                  onClick={() => navigate('/devis')}
                  data-testid="hero-primary-cta"
                >
                  Recevoir mon devis cadré
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
                
                <Button
                  size="lg"
                  variant="outline"
                  className="rounded-xl px-8 py-6 text-base font-medium border-[color:var(--hr-border)]"
                  onClick={() => navigate('/galerie-avant-apres')}
                  data-testid="hero-secondary-cta"
                >
                  Voir des transformations
                </Button>
              </div>

              {/* Proof strip */}
              <div className="grid grid-cols-3 gap-4 pt-6 border-t" style={{ borderColor: 'var(--hr-border)' }}>
                {[
                  { icon: Clock, text: 'Planning respecté' },
                  { icon: Users, text: 'Coordination totale' },
                  { icon: Shield, text: 'Qualité garantie' }
                ].map((item, i) => (
                  <div key={i} className="flex items-center gap-2" data-testid={`proof-${i}`}>
                    <item.icon className="h-5 w-5" style={{ color: 'var(--hr-accent)' }} />
                    <span className="text-sm font-medium" style={{ color: 'var(--hr-text)' }}>
                      {item.text}
                    </span>
                  </div>
                ))}
              </div>
            </div>

            {/* Right: Image (mobile: avant le texte pour image mentale immédiate) */}
            <div className="lg:col-span-6 order-first lg:order-last">
              <div className="media-frame">
                <img 
                  src={HERO_IMAGE}
                  alt="Rénovation intérieure moderne haut de gamme"
                  className="w-full h-auto"
                  loading="eager"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CONTRASTE ÉMOTIONNEL - Ce que vous ne voulez plus */}
      <section className="py-16 sm:py-20" style={{ backgroundColor: 'var(--hr-bg-alt)' }}>
        <div className="max-w-[1160px] mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mx-auto text-center space-y-6">
            <p className="font-['Montserrat'] text-xs font-medium uppercase tracking-wide" style={{ color: 'var(--hr-text-muted)' }}>
              Le problème
            </p>
            <h2 className="font-['Playfair_Display'] text-3xl sm:text-4xl font-semibold" style={{ color: 'var(--hr-text)' }}>
              Les travaux, c'est souvent ça :
            </h2>
            
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-6 text-left">
              {[
                'Des artisans qui ne se parlent pas',
                'Un planning qui explose',
                'Des imprévus à chaque étape',
                'Vous qui coordonnez tout seul',
                'Un stress permanent',
                'Un résultat décevant'
              ].map((pain, i) => (
                <div key={i} className="flex items-start gap-3">
                  <div className="mt-1 h-1.5 w-1.5 rounded-full flex-shrink-0" style={{ backgroundColor: 'var(--hr-danger)' }} />
                  <p className="text-base" style={{ color: 'var(--hr-text-muted)' }}>{pain}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* NOTRE APPROCHE - Contraste positif */}
      <section className="py-16 sm:py-20">
        <div className="max-w-[1160px] mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mx-auto text-center space-y-6 mb-12">
            <p className="font-['Montserrat'] text-xs font-medium uppercase tracking-wide" style={{ color: 'var(--hr-accent)' }}>
              Notre approche
            </p>
            <h2 className="font-['Playfair_Display'] text-3xl sm:text-4xl font-semibold" style={{ color: 'var(--hr-text)' }}>
              Avec Homely, c'est différent :
            </h2>
            <p className="text-lg leading-relaxed" style={{ color: 'var(--hr-text-muted)' }}>
              Nous ne faisons pas que des travaux. Nous pilotons votre rénovation de A à Z.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              {
                icon: Users,
                title: 'Un seul interlocuteur',
                desc: 'Vous ne gérez plus 5 artisans. Nous coordonnons tout.'
              },
              {
                icon: FileCheck,
                title: 'Planning validé',
                desc: 'Dates fixées, étapes claires, délais respectés.'
              },
              {
                icon: Shield,
                title: 'Contrôle qualité',
                desc: 'Suivi hebdomadaire, points de contrôle, réception propre.'
              },
              {
                icon: CheckCircle2,
                title: 'Devis cadré',
                desc: 'Pas de mauvaise surprise. Tout est anticipé.'
              }
            ].map((item, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: i * 0.1 }}
                viewport={{ once: true }}
              >
                <Card className="rounded-2xl border-[color:var(--hr-border)] h-full hover:shadow-[0_18px_48px_rgba(43,43,43,0.10)] transition-shadow">
                  <CardContent className="p-6 space-y-4">
                    <div className="h-12 w-12 rounded-xl flex items-center justify-center" style={{ backgroundColor: 'var(--hr-bg-alt)' }}>
                      <item.icon className="h-6 w-6" style={{ color: 'var(--hr-primary)' }} />
                    </div>
                    <h3 className="font-['Figtree'] text-lg font-semibold" style={{ color: 'var(--hr-text)' }}>
                      {item.title}
                    </h3>
                    <p className="text-sm leading-relaxed" style={{ color: 'var(--hr-text-muted)' }}>
                      {item.desc}
                    </p>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* AVANT/APRÈS - IMAGE MENTALE */}
      <section className="py-16 sm:py-20" style={{ backgroundColor: 'var(--hr-bg-alt)' }}>
        <div className="max-w-[1160px] mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center space-y-6 mb-12">
            <p className="font-['Montserrat'] text-xs font-medium uppercase tracking-wide" style={{ color: 'var(--hr-text-muted)' }}>
              Résultat
            </p>
            <h2 className="font-['Playfair_Display'] text-3xl sm:text-4xl font-semibold" style={{ color: 'var(--hr-text)' }}>
              Des transformations concrètes
            </h2>
            <p className="text-lg leading-relaxed max-w-2xl mx-auto" style={{ color: 'var(--hr-text-muted)' }}>
              Voici ce qu'une rénovation pilotée peut donner. Même pièce. Résultat haut de gamme.
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
            <Card className="rounded-2xl border-[color:var(--hr-border)]">
              <CardContent className="p-0">
                <div className="media-frame">
                  <img src={BATHROOM_IMAGE} alt="Salle de bain rénovée" className="w-full h-auto" />
                </div>
                <div className="p-6 space-y-2">
                  <h3 className="font-['Playfair_Display'] text-xl font-semibold" style={{ color: 'var(--hr-text)' }}>
                    Salle de bain premium - Genève
                  </h3>
                  <p className="text-sm" style={{ color: 'var(--hr-text-muted)' }}>
                    6 semaines • Coordination complète
                  </p>
                  <p className="text-sm leading-relaxed pt-2" style={{ color: 'var(--hr-text)' }}>
                    <span className="font-medium">Ce qui a été piloté :</span> Plombiers, électriciens, carreleurs. Suivi hebdomadaire. Réception propre.
                  </p>
                </div>
              </CardContent>
            </Card>

            {/* Placeholder pour 2ème cas */}
            <Card className="rounded-2xl border-[color:var(--hr-border)] flex items-center justify-center" style={{ backgroundColor: 'var(--hr-bg)' }}>
              <CardContent className="p-12 text-center space-y-4">
                <p className="text-lg font-medium" style={{ color: 'var(--hr-text-muted)' }}>
                  + Plus de transformations dans la galerie
                </p>
                <Button
                  variant="outline"
                  className="rounded-xl"
                  onClick={() => navigate('/galerie-avant-apres')}
                >
                  Voir tous les projets
                </Button>
              </CardContent>
            </Card>
          </div>

          <div className="text-center">
            <Button
              size="lg"
              className="rounded-xl px-8"
              style={{ backgroundColor: 'var(--hr-primary)', color: 'var(--hr-primary-fg)' }}
              onClick={() => navigate('/galerie-avant-apres')}
              data-testid="gallery-cta"
            >
              Découvrir toutes les transformations
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </div>
        </div>
      </section>

      {/* MÉTHODE - CLARTÉ/CONTRÔLE */}
      <section className="py-16 sm:py-20">
        <div className="max-w-[1160px] mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center space-y-6 mb-12">
            <p className="font-['Montserrat'] text-xs font-medium uppercase tracking-wide" style={{ color: 'var(--hr-accent)' }}>
              Notre méthode
            </p>
            <h2 className="font-['Playfair_Display'] text-3xl sm:text-4xl font-semibold" style={{ color: 'var(--hr-text)' }}>
              Comment on s'y prend
            </h2>
            <p className="text-lg leading-relaxed max-w-2xl mx-auto" style={{ color: 'var(--hr-text-muted)' }}>
              Une rénovation pilotée, c'est un processus clair. Pas d'improvisation.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-5 gap-6">
            {[
              { num: '01', title: 'Diagnostic', desc: 'Visite sur place, analyse de vos besoins' },
              { num: '02', title: 'Devis cadré', desc: 'Chiffrage détaillé, pas de surprise' },
              { num: '03', title: 'Planning', desc: 'Dates fixées, jalons clairs' },
              { num: '04', title: 'Coordination', desc: 'Suivi hebdo, contrôle qualité' },
              { num: '05', title: 'Réception', desc: 'Livraison propre, garanties' }
            ].map((step, i) => (
              <motion.div
                key={i}
                className="relative"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: i * 0.1 }}
                viewport={{ once: true }}
              >
                <Card className="rounded-2xl border-[color:var(--hr-border)] h-full">
                  <CardContent className="p-6 space-y-3">
                    <div 
                      className="font-['Playfair_Display'] text-3xl font-bold"
                      style={{ color: 'var(--hr-accent)' }}
                    >
                      {step.num}
                    </div>
                    <h3 className="font-['Figtree'] text-base font-semibold" style={{ color: 'var(--hr-text)' }}>
                      {step.title}
                    </h3>
                    <p className="text-sm leading-relaxed" style={{ color: 'var(--hr-text-muted)' }}>
                      {step.desc}
                    </p>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA FINAL - CONVERSION */}
      <section className="py-20 sm:py-24" style={{ backgroundColor: 'var(--hr-primary)' }}>
        <div className="max-w-[1160px] mx-auto px-4 sm:px-6 lg:px-8 text-center space-y-8">
          <h2 
            className="font-['Playfair_Display'] text-3xl sm:text-4xl lg:text-5xl font-semibold"
            style={{ color: 'var(--hr-primary-fg)' }}
          >
            Prêt à rénover sereinement ?
          </h2>
          <p 
            className="text-lg sm:text-xl leading-relaxed max-w-2xl mx-auto"
            style={{ color: 'rgba(245,243,239,0.9)' }}
          >
            Recevez un devis détaillé et un plan d'action clair. Sans engagement.
          </p>
          <Button
            size="lg"
            className="rounded-xl px-10 py-6 text-lg font-medium"
            style={{ backgroundColor: 'var(--hr-bg)', color: 'var(--hr-text)' }}
            onClick={() => navigate('/devis')}
            data-testid="final-cta"
          >
            Demander mon devis cadré
            <ArrowRight className="ml-2 h-6 w-6" />
          </Button>
        </div>
      </section>
    </div>
  );
}